/*globals views, login, globalNavigate */
import React, { useEffect, useState, useRef, useContext } from "react";
import styled from "styled-components";
import * as d3 from "d3";

import ActionsContext from "common/ActionsContext";
import colors from "common/graphs/colors";
import colorsDark from "common/graphs/colorsDark";
import Request from "common/Request";
import RangeSliderInput from "common/RangeSliderInput";
import Charts from "./Charts";
import {
  getInRangeItems,
  getMetricDir,
  parseStatsResponse,
  getSampleSize,
  retrieveRange,
  getTargets,
  heatmapFormatMap,
} from "./utils";
import { PERIOD_TO_HOURS } from "common/constants";
import { getBqnIdCmdPart } from "common/api";
import { metricMap, yAxisUnitMap } from "../common";

const periodAsInterval = {
  oneDay: 10,
  oneWeek: 10,
  oneMonth: 60,
  threeMonths: 60,
};

const tooltipUnitMap = {
  averageSpeed: "Mbps",
  maxSpeed: "Mbps",
  activeFlows: "flows",
  flowsCreated: "flows/minute",
  latency: "ms",
  "latency-med": "ms",
  "latency-80p": "ms",
  retransmissions: "%",
  congestion: "%",
  traffic: "%",
};

const CenteredMessage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1 1 100%;
  min-height: 155px;
`;

const EndSpace = styled.div`
  height: 20px;
  `;

const NoSubscribersSelected = () =>
  <CenteredMessage>No Subscribers selected.</CenteredMessage>

const returnTo = "viewStatusSubscribers";

const openSubscriberDetails = (entity, key, timePeriod) => {
  const target =
    entity === "ip-address"
      ? { addr: key, subsId: null }
      : { addr: null, subsId: key };
  views.doKeep(returnTo);
  globalNavigate("viewStatusSubscribers", { ...target, returnView: returnTo, timePeriod });
};

const TimeEvolutionMetricsResult = ({
  direction,
  timePeriod,
  metric,
  entity,
  subscribersToPlot = null,
  mode,
  bqnId,
  meanSorting,
  setMeanSorting,
  setFieldsMeanMap,
}) => {
  const actions = useContext(ActionsContext);
  const windowWidth = window.innerWidth;
  const [request, setRequest] = useState(null);
  const [range, setRange] = useState();
  const [initialRange, setInitialRange] = useState();
  const context = useRef({ entity, metric, timePeriod, direction });
  const yAxisUnits = yAxisUnitMap[metric];
  const POINTS_PER_TILE_CHART = windowWidth / 5;
  const colorsSet = login.isTheme("light") ? colors : colorsDark;

  const doLoad = () => {
    setRequest(loadStatistics());
  };

  useEffect(() => {
    removeTooltips();
  }, [request]);

  useEffect(() => {
    return setRange(initialRange);
  }, [initialRange]);

  useEffect(()=> {
    return actions.recv("do-load", () =>
      doLoad()
    );
  },[])

  useEffect(() => {
    context.current = {
      ...context.current,
      entity,
      metric,
      timePeriod,
      direction,
      subscribersToPlot,
    };
    doLoad();
  }, [metric, timePeriod, entity, subscribersToPlot.map(s => s.id).join(','), direction]);

  const loadStatistics = () => {
    const { entity, metric, timePeriod, direction, subscribersToPlot } =
      context.current;
    const hours = PERIOD_TO_HOURS[timePeriod];
    if (subscribersToPlot === null || subscribersToPlot.length === 0) {
      return Promise.resolve({ noSubscribers: true, fields: [], items: [] });
    }

    return retrieveRange(hours)
    .then(({ from, to }) => {
      const targets =
      getTargets(entity, subscribersToPlot);
      setInitialRange({ from, to });
      return ifCl
        .run(
          `show statistics subscribers ${getMetricDir(metric, direction)} ${
            entity === "ip-address" ? "" : "id "
          }'${targets.join(",")}' hours ${hours} interval ${
            periodAsInterval[timePeriod] || 10
          }${getBqnIdCmdPart(mode, bqnId)}`
        )
        .then((response) => {
          return parseStatsResponse(response, metric, from, to, colorsSet)
        })
    })
    .catch((error)=>showModalError("Error:", error))
  };

  function removeTooltips() {
    for (const element of document.getElementsByClassName(
      "nvtooltip xy-tooltip"
    )) {
      element.remove();
    }
  }

  const formatUnit = (metric) => (value) =>
    value === null || value === undefined
      ? "n/a"
      : `${
          ["averageSpeed", "maxSpeed"].includes(metric)
            ? value.toFixed(3)
            : value
        } ${tooltipUnitMap[metric]}`;

  return (
    <>
      {request === null ? null : (
        <Request during={request}>
          {({ noSubscribers= false, fields, items, limits: initLimits }) => {
            if ( noSubscribers === true ) {
              return <NoSubscribersSelected />
            }
            const filteredFields = fields.filter(({name}) => name !== "DOWNLINK" && name !== "UPLINK" && name !== "network")
            const selectedRange = range || initialRange;
            const sampleSize = getSampleSize(
              selectedRange,
              POINTS_PER_TILE_CHART,
              timePeriod
            );
            const { items: inRangeItems, limits, sampledValues } = getInRangeItems(
              items,
              selectedRange,
              filteredFields,
              sampleSize,
              initLimits
            );

            return (
              <Charts
                inRangeItems={inRangeItems}
                yAxisUnits={yAxisUnits}
                tooltipUnits={tooltipUnitMap[metric]}
                xAxisFormat={d3.timeFormat("%m/%d %H:%M")}
                yTooltipFormat={formatUnit(metric)}
                fields={filteredFields}
                onSeriesInspect={(key) => openSubscriberDetails(entity, key, timePeriod)}
                rangeY={
                  limits?.isZeroOrNull ? [0, 1] : [0, limits ? limits.max : 1]
                }
                sampledValues={sampledValues}
                heatmapFormat={heatmapFormatMap[metric]}
                entity={entity}
                metric={metricMap[metric]}
                subscribersToPlot={subscribersToPlot}
                meanSorting={meanSorting}
                setMeanSorting={setMeanSorting}
                setFieldsMeanMap={setFieldsMeanMap}
              />
            );
          }}
        </Request>
      )}
      <div className="full-width can-compress margin-t-20 margin-b-20" style={{paddingLeft: 190, marginRight: -15}}>
        {initialRange && (
          <RangeSliderInput
            {...initialRange}
            onChange={(value) => setRange(value)}
          />
        )}
      </div>
      <EndSpace />
    </>
  );
};

export default TimeEvolutionMetricsResult;
